import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { formatNumber, getCurrencySymbolFromCache } from "../../../../../utils";
import {
  deliveryCostAmount,
  effectiveDeliveryCost,
  hasDeliveryCost,
  requiresDeliveryCost,
  type DeliveryCostValue,
} from "../../../../../utils/delivery-cost";
import {
  compareTerm,
  formatOfferDateTime,
  formatQuantityRange,
  formatTotalPrice,
  formatUnitPrice,
  hasDepositRound,
  isDeliveryRound,
  offerOriginalSource,
  offerStatusLabelKey,
  proposalOrganisationName,
  resolveDeliveryLocation,
  TermChange,
} from "../../../utils";
import { DeliveryLookups } from "../../../types";
import { Offer, OfferStatus } from "../../OfferCard/interfaces";
import { NegotiationProposal } from "../NegotiatedDetailsCard/offer-negotiation-proposal";

interface OfferTermsComparisonProps {
  offer: Offer;
  proposals: NegotiationProposal[];
  deliveryLookups: DeliveryLookups;
  round: number;
  onRoundChange: (round: number) => void;
}

interface Terms {
  price: number;
  minimumQuantity: number;
  maximumQuantity: number;
  deliveryCost: DeliveryCostValue;
  deliveryLocation?: string;
  plannedExchangeTime?: string;
}

const toTerms = (source: Offer | NegotiationProposal, offer: Offer): Terms => ({
  price: Number(source.price),
  minimumQuantity: Number(source.minimum_quantity),
  maximumQuantity: Number(source.maximum_quantity || source.minimum_quantity),
  deliveryCost: effectiveDeliveryCost(source, offer),
  deliveryLocation: source.delivery_location,
  plannedExchangeTime: source.planned_exchange_time,
});

const formatTermsQuantity = (terms: Terms, unit: string): string =>
  `${formatQuantityRange(terms.minimumQuantity, terms.maximumQuantity)} ${unit}`;

const formatTermsTotal = (terms: Terms, currency: string): string =>
  formatTotalPrice(
    {
      price: terms.price,
      minimum_quantity: terms.minimumQuantity,
      maximum_quantity: terms.maximumQuantity,
      delivery_cost: terms.deliveryCost,
    },
    currency
  );

const termsTotalValue = (terms: Terms): number =>
  terms.price * terms.minimumQuantity + deliveryCostAmount(terms.deliveryCost);

const deltaIndicator = (change: TermChange | null, format: (value: number) => string) =>
  change && (
    <span className="flex items-center gap-0.5 whitespace-nowrap font-medium text-info-500 text-xs">
      {change.direction === "up" ? (
        <ArrowUp className="size-3" />
      ) : (
        <ArrowDown className="size-3" />
      )}
      {format(Math.abs(change.absolute))}
    </span>
  );

export const OfferTermsHeadline = ({
  offer,
  proposal,
}: {
  offer: Offer;
  proposal?: NegotiationProposal;
}) => {
  const { t } = useTranslation();

  const currency = getCurrencySymbolFromCache() || offer.currency?.symbol;
  const unit = offer.unit_of_measure?.code;
  const originalOffer = offerOriginalSource(offer);
  const terms = proposal ? toTerms(proposal, offer) : toTerms(originalOffer, originalOffer);

  return (
    <div className="flex flex-col gap-2 px-6 pt-4 pr-12 pb-3">
      <span className="font-semibold text-3xl text-foreground">
        {formatTermsTotal(terms, currency)}
      </span>
      <span className="text-muted-foreground text-sm">
        {t("offerDetail.headlineTerms", {
          quantity: formatTermsQuantity(terms, unit),
          price: `${currency}${formatNumber(terms.price)}`,
        })}
        {deliveryCostAmount(terms.deliveryCost) > 0 &&
          ` ${t("representativeOffers.card.plusDeliveryCost", {
            amount: `${currency}${formatNumber(deliveryCostAmount(terms.deliveryCost))}`,
          })}`}
      </span>
    </div>
  );
};

export const OfferTermsComparison = ({
  offer,
  proposals,
  deliveryLookups,
  round,
  onRoundChange,
}: OfferTermsComparisonProps) => {
  const { t } = useTranslation();

  // Round -1 is the offer itself, held against the stock collection it was raised on.
  const depositRound = hasDepositRound(offer);
  const isOfferRound = round < 0 && depositRound;
  const proposal: NegotiationProposal | undefined = proposals[round];
  const previous: NegotiationProposal | undefined = round > 0 ? proposals[round - 1] : undefined;
  const isLatestRound = round === proposals.length - 1;

  const currency = getCurrencySymbolFromCache() || offer.currency?.symbol;
  const unit = offer.unit_of_measure?.code;

  const depositQuantity = Number(offer.deposit?.original_quantity ?? offer.deposit?.quantity);
  const depositTerms: Terms = {
    price: Number(offer.deposit?.price),
    minimumQuantity: depositQuantity,
    maximumQuantity: depositQuantity,
    deliveryCost: null,
  };

  const originalOffer = offerOriginalSource(offer);
  const originalOfferTerms = toTerms(originalOffer, originalOffer);
  const original = isOfferRound
    ? depositTerms
    : previous
      ? toTerms(previous, offer)
      : originalOfferTerms;
  const counter = isOfferRound
    ? originalOfferTerms
    : proposal
      ? toTerms(proposal, offer)
      : undefined;

  const quantityChanged =
    !!counter &&
    (counter.minimumQuantity !== original.minimumQuantity ||
      counter.maximumQuantity !== original.maximumQuantity);
  const dateChanged =
    !isOfferRound && !!counter && counter.plannedExchangeTime !== original.plannedExchangeTime;

  const priceChange = counter ? compareTerm(original.price, counter.price) : null;
  const totalChange = counter
    ? compareTerm(termsTotalValue(original), termsTotalValue(counter))
    : null;
  const deliveryCostChange =
    counter && !isOfferRound
      ? compareTerm(
          deliveryCostAmount(original.deliveryCost),
          deliveryCostAmount(counter.deliveryCost)
        )
      : null;
  const showDeliveryCost =
    hasDeliveryCost(original.deliveryCost) || (!!counter && hasDeliveryCost(counter.deliveryCost));
  const quantityChange = counter
    ? compareTerm(original.minimumQuantity, counter.minimumQuantity)
    : null;

  const formatPrice = (terms: Terms) => formatUnitPrice(terms.price, currency, unit);
  const formatQuantity = (terms: Terms) => formatTermsQuantity(terms, unit);
  const formatDeliveryCost = (terms: Terms) =>
    hasDeliveryCost(terms.deliveryCost)
      ? `${currency}${formatNumber(deliveryCostAmount(terms.deliveryCost))}`
      : t(
          requiresDeliveryCost(terms.deliveryLocation)
            ? "termsComparison.notSpecified"
            : "termsComparison.notApplicable"
        );

  const locationLabels = {
    nameUnavailable: t("offerCard.nameUnavailable"),
    addressUnavailable: t("offerCard.addressUnavailable"),
    unknownDeliveryType: t("offerCard.unknownDeliveryType"),
  };
  // A stock collection carries no delivery terms of its own.
  const notApplicable = t("termsComparison.notApplicable");
  const originalLocation = isOfferRound
    ? [notApplicable]
    : resolveDeliveryLocation(
        offer,
        previous ?? offer.original_terms,
        deliveryLookups,
        locationLabels
      );
  const counterLocation = isOfferRound
    ? resolveDeliveryLocation(offer, offer.original_terms, deliveryLookups, locationLabels)
    : proposal
      ? resolveDeliveryLocation(offer, proposal, deliveryLookups, locationLabels)
      : undefined;

  // Addresses run long, so they wrap and stack instead of widening the table.
  const stackedLines = (lines: string[]) => (
    <span className="flex flex-col whitespace-normal">
      {lines.map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </span>
  );

  const rows = [
    {
      key: "totalValue",
      changed: !!totalChange,
      original: formatTermsTotal(original, currency),
      counter: counter && formatTermsTotal(counter, currency),
      indicator: deltaIndicator(totalChange, value => `${currency}${formatNumber(value)}`),
    },
    {
      key: "quantity",
      changed: quantityChanged,
      original: formatQuantity(original),
      counter: counter && formatQuantity(counter),
      indicator: deltaIndicator(quantityChange, value => `${formatNumber(value)} ${unit}`),
    },
    {
      key: "unitPrice",
      changed: !!priceChange,
      original: formatPrice(original),
      counter: counter && formatPrice(counter),
      indicator: deltaIndicator(priceChange, value => formatUnitPrice(value, currency, unit)),
    },
    ...(showDeliveryCost
      ? [
          {
            key: "deliveryCost",
            changed: !!deliveryCostChange,
            original: formatDeliveryCost(original),
            counter: counter && formatDeliveryCost(counter),
            indicator: deltaIndicator(
              deliveryCostChange,
              value => `${currency}${formatNumber(value)}`
            ),
          },
        ]
      : []),
    {
      key: "delivery",
      changed: dateChanged,
      original: isOfferRound ? notApplicable : formatOfferDateTime(original.plannedExchangeTime),
      counter: counter && formatOfferDateTime(counter.plannedExchangeTime),
      indicator: null,
    },
    {
      key: "location",
      changed:
        !isOfferRound && !!counterLocation && counterLocation.join() !== originalLocation.join(),
      original: stackedLines(originalLocation),
      counter: counterLocation && stackedLines(counterLocation),
      indicator: null,
    },
  ];

  const offerColumn = t("termsComparison.offerColumn", { name: offer.trader?.name });
  const baselineColumn = isOfferRound
    ? t("offerDetail.deposit")
    : previous
      ? t("termsComparison.previousColumn", {
          name: proposalOrganisationName(offer, previous),
        })
      : offerColumn;
  const counterName = proposal ? proposalOrganisationName(offer, proposal) : undefined;
  const counterColumn = isOfferRound
    ? offerColumn
    : t(
        isDeliveryRound(offer, round, proposals.length)
          ? "termsComparison.deliveryColumn"
          : "termsComparison.counterColumn",
        { name: counterName }
      );

  const paymentPendingBadge = offer.status === OfferStatus.ExchangePaymentPending && (
    <Badge variant="warning">{t(offerStatusLabelKey(offer.status))}</Badge>
  );

  return (
    <div className="mx-6 my-4 overflow-hidden rounded-lg border border-border">
      {counter ? (
        <>
          <div className="flex items-center justify-between gap-2 border-border border-b bg-muted/40 px-4 py-2.5">
            <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
              {isOfferRound
                ? t("termsComparison.offerHeader", {
                    round: 1,
                    date: formatOfferDateTime(offer.publishing_datetime ?? offer.publishing_date),
                  })
                : t("termsComparison.roundHeader", {
                    round: round + (depositRound ? 2 : 1),
                    date: formatOfferDateTime(proposal?.backend_creation_time),
                  })}
            </span>
            <div className="flex items-center gap-1">
              <WFPButton
                variant="ghost"
                size="icon-sm"
                aria-label={t("termsComparison.previousRound")}
                disabled={round <= (depositRound ? -1 : 0)}
                onClick={() => onRoundChange(round - 1)}
              >
                <ChevronLeft />
              </WFPButton>
              <span className="text-muted-foreground text-xs">
                {t("termsComparison.round", {
                  current: round + (depositRound ? 2 : 1),
                  total: proposals.length + (depositRound ? 1 : 0),
                })}
              </span>
              <WFPButton
                variant="ghost"
                size="icon-sm"
                aria-label={t("termsComparison.nextRound")}
                disabled={isLatestRound}
                onClick={() => onRoundChange(round + 1)}
              >
                <ChevronRight />
              </WFPButton>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="hidden px-4 md:table-cell" />
                <TableHead className="whitespace-normal px-4 py-3 text-foreground text-sm">
                  {baselineColumn}
                </TableHead>
                <TableHead className="whitespace-normal border-border border-l bg-info-50 px-4 py-3 text-info-700 text-sm">
                  {counterColumn}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.key} className="hover:bg-transparent">
                  <TableCell className="hidden px-4 py-3 align-top font-normal text-muted-foreground text-sm md:table-cell">
                    {t(`termsComparison.${row.key}`)}
                  </TableCell>
                  <TableCell className="px-4 py-3 align-top text-foreground text-sm">
                    <span className="mb-1 block font-normal text-muted-foreground text-xs md:hidden">
                      {t(`termsComparison.${row.key}`)}
                    </span>
                    {row.original}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "border-border border-l bg-info-50 px-4 py-3 align-top text-sm",
                      row.changed ? "font-bold text-info-700" : "text-foreground"
                    )}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      {row.counter}
                      {row.indicator}
                      {row.key === "totalValue" && paymentPendingBadge}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <dl className="divide-y divide-border">
          {rows.map(row => (
            <div key={row.key} className="flex justify-between gap-4 px-4 py-3">
              <dt className="text-muted-foreground text-sm">{t(`termsComparison.${row.key}`)}</dt>
              <dd className="flex flex-wrap items-center justify-end gap-2 text-right font-semibold text-base text-foreground">
                {row.original}
                {row.key === "totalValue" && paymentPendingBadge}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
